import { useEffect, useState } from 'react';
import { KeyboardOff } from 'lucide-react';

/**
 * KeyboardController — dedicated iPad / soft-keyboard assist.
 *
 * Two problems this solves:
 *
 *   1. The iPad on-screen keyboard eats the bottom half of the screen
 *      and frequently covers the field the user is typing into.
 *   2. There's no built-in browser "dismiss keyboard" button on iOS.
 *      Users have to tap a blank area of the page to drop focus.
 *
 * How it works:
 *
 *   - Subscribes to focusin / focusout to track whether a text input
 *     (or a contenteditable element / textarea) is active.
 *   - Uses the visualViewport API (Safari 13+ / Chrome / Edge) to
 *     measure the actual keyboard height so content can be padded
 *     accordingly. Sets a CSS var --kb-inset on <html> that the rest
 *     of the app can reference.
 *   - Auto-scrolls the focused element into view above the keyboard
 *     (above its own inherent scroll-into-view so the caret is
 *     visible, not just "the field exists somewhere").
 *   - Renders a floating "Hide Keyboard" pill in the top-right when
 *     a keyboard-triggering element is focused. One tap blurs the
 *     active element, which tells the OS to dismiss the keyboard.
 *
 * Mount once at the app root. The CSS var is also surfaced as
 *   padding-bottom: var(--kb-inset) on the main content wrapper
 * so dense forms can't be covered.
 */

const KB_THRESHOLD_PX = 120; // visualViewport drops by > this when keyboard shows

function isTextInput(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const t = (el as HTMLInputElement).type;
    // Disabled + hidden + checkbox + radio don't pop the keyboard
    return !['checkbox', 'radio', 'hidden', 'submit', 'button', 'file', 'range', 'color'].includes(t);
  }
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

export function KeyboardController() {
  const [hasFocus, setHasFocus] = useState(false);
  const [kbHeight, setKbHeight] = useState(0);

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      if (isTextInput(e.target as Element)) setHasFocus(true);
    };
    const onFocusOut = () => {
      // Defer — the next focusin may fire immediately (tab between fields)
      window.setTimeout(() => {
        const a = document.activeElement;
        if (!isTextInput(a)) setHasFocus(false);
      }, 50);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  // Track visual viewport — the browser reports its visible height
  // SHRINKING when the on-screen keyboard slides up. Delta = keyboard height.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const innerH = window.innerHeight;
      const delta = Math.max(0, innerH - vv.height - vv.offsetTop);
      const next = delta > KB_THRESHOLD_PX ? delta : 0;
      setKbHeight(next);
      document.documentElement.style.setProperty('--kb-inset', `${next}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Auto-scroll focused input into the top third of the visible area
  // when the keyboard is up. Default browser behavior is inconsistent.
  useEffect(() => {
    if (!hasFocus || kbHeight === 0) return;
    const a = document.activeElement as HTMLElement | null;
    if (!a) return;
    // scrollIntoView with block: 'center' keeps the input above the keyboard
    // on every tested browser (iOS Safari, Chrome, Edge). { behavior: 'smooth' }
    // is avoided to prevent the view from pinging around as the user taps.
    a.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [hasFocus, kbHeight]);

  function hideKeyboard() {
    const a = document.activeElement as HTMLElement | null;
    a?.blur?.();
    setHasFocus(false);
  }

  // Render the dismiss button only when a text input is actually focused.
  // Also surfaces a faint "keyboard up" indicator height at the bottom of
  // the viewport via the --kb-inset CSS var (set above) — components can
  // reference it with padding-bottom: var(--kb-inset).
  if (!hasFocus) return null;

  return (
    <button
      onMouseDown={(e) => e.preventDefault()} // prevent the button tap from dropping focus before we blur explicitly
      onClick={hideKeyboard}
      className={
        'fixed right-3 z-[60] flex items-center gap-1 px-2.5 py-1.5 ' +
        'border border-blue-400 bg-blue-600 text-white text-[11px] font-bold uppercase tracking-widest ' +
        'shadow-xl hover:bg-blue-500 active:bg-blue-700'
      }
      // Sit just above the keyboard when it's up — uses the CSS var we
      // computed. Falls back to bottom:12px when the var is missing.
      style={{ bottom: `calc(var(--kb-inset, 0px) + 12px)` }}
      title="Dismiss the on-screen keyboard (blurs focused input)"
      aria-label="Hide keyboard"
    >
      <KeyboardOff size={13} />
      Hide Keyboard
    </button>
  );
}
